<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  CreateStepInput,
  CreateStepInputMutation,
  CreateStepInputMutationVariables,
  CreateStepOutput,
  CreateStepOutputMutation,
  CreateStepOutputMutationVariables,
  DeleteStepOutputMutation,
  DeleteStepOutputMutationVariables,
  StepOutputsSectionDataQuery,
  StepOutputsSectionDataQueryVariables,
  StepOutputsSectionPlacementDataQuery,
  StepOutputsSectionPlacementDataQueryVariables,
  UpdateStepOutput,
  UpdateStepOutputMutation,
  UpdateStepOutputMutationVariables,
} from '@/generated/graphql'
import { computed, reactive, ref } from 'vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import useVuelidate from '@vuelidate/core'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import MilliQuantityField from '@/components/input/MilliQuantityField.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import DeleteDialogNext from '@/components/dialogs/AsyncDeleteDialog.vue'
import ItemDeleteIcon from '@/components/button/ButtonDelete.vue'
import { mockCreateStepOutput, mockUpdateStepOutput } from '@/generated/graphql-mocks'
import ArticleName from '@/components/article/ArticleName.vue'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import ArticlePicker from '@/components/input/ArticlePicker.vue'
import ModulePicker from '@/components/input/ModulePicker.vue'
import StepPicker from '@/components/input/StepPicker.vue'
import { itemSortByName } from '@/app'
import { required } from '@/validation'
import { helpers } from '@vuelidate/validators'

const props = defineProps<{
  moduleId?: string
  stepId: string
  // Enables the save & assign flow; without a BOP there is no step to assign to.
  bopId?: string
  // When set, outputs are local to this library-reference placement instead of the step.
  graphNodeStepId?: string
}>()

type LocalStepOutput = StepOutputsSectionDataQuery['product']['step']['outputs'][0]

const { t } = useI18n()

const stepFetchQuery = useQuery<StepOutputsSectionDataQuery, StepOutputsSectionDataQueryVariables>(
  gql`
    query StepOutputsSectionData($stepId: ID!) {
      product {
        step(id: $stepId) {
          outputs {
            id
            milliQuantity
            article {
              id
              name
              articleNumber
              revision
              unit
            }
          }
        }
      }
    }
  `,
  () => ({ stepId: props.stepId }),
  () => ({ enabled: !props.graphNodeStepId }),
)

const placementFetchQuery = useQuery<
  StepOutputsSectionPlacementDataQuery,
  StepOutputsSectionPlacementDataQueryVariables
>(
  gql`
    query StepOutputsSectionPlacementData($graphNodeStepId: ID!) {
      product {
        placementStepOutputs(graphNodeStepId: $graphNodeStepId) {
          id
          milliQuantity
          article {
            id
            name
            articleNumber
            revision
            unit
          }
        }
      }
    }
  `,
  () => ({ graphNodeStepId: props.graphNodeStepId ?? '' }),
  () => ({ enabled: !!props.graphNodeStepId }),
)

const loading = computed(() => stepFetchQuery.loading.value || placementFetchQuery.loading.value)
const rawOutputs = computed<LocalStepOutput[]>(() =>
  props.graphNodeStepId
    ? ((placementFetchQuery.result.value?.product.placementStepOutputs ?? []) as LocalStepOutput[])
    : (stepFetchQuery.result.value?.product.step.outputs ?? []),
)
const outputs = computed(() => itemSortByName(rawOutputs.value, (i) => i?.article?.articleNumber))
const refetch = () => {
  if (props.graphNodeStepId) placementFetchQuery.refetch()
  else stepFetchQuery.refetch()
}

type CreateModel = Partial<CreateStepOutput>
const createModelKeys = Object.keys(mockCreateStepOutput())
const createModel = reactive<CreateModel>({})
const createValidation = useVuelidate<CreateModel>(
  {
    articleId: { required },
    milliQuantity: { required },
  },
  createModel,
)
const createMutation = useMutation<CreateStepOutputMutation, CreateStepOutputMutationVariables>(gql`
  mutation CreateStepOutput($command: CreateStepOutput!) {
    product {
      createStepOutput(command: $command) {
        id
      }
    }
  }
`)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const assignAfterSave = ref(false)
const doCreate = async () => {
  createModelKeys.forEach((k) => {
    ;(createModel as Record<string, unknown>)[k] = undefined
  })
  createModel.moduleId = props.moduleId
  createModel.stepId = props.stepId
  createModel.graphNodeStepId = props.graphNodeStepId
  assignAfterSave.value = false

  let createdOutputId: string | undefined
  await createDialog.value?.open(() =>
    createMutation.mutate({ command: createModel as CreateStepOutput }).then((result) => {
      createdOutputId = result?.data?.product.createStepOutput.id
      refetch()
    }),
  )
  if (assignAfterSave.value && createdOutputId) {
    await doAssign(createdOutputId, createModel.milliQuantity as number)
  }
}
// The save closure bails on invalid input; drop the flag so a later plain save stays a plain save.
function saveAndAssign(save: () => void) {
  assignAfterSave.value = true
  save()
  if (createValidation.value.$invalid) {
    assignAfterSave.value = false
  }
}

const assignModuleId = ref<string>()
type AssignModel = Partial<CreateStepInput>
const assignModel = reactive<AssignModel>({})
const assignValidationRules = computed(() => ({
  stepId: {
    required,
    differentStep: helpers.withMessage(
      () => t('component.stepOutputsSection.assignSameStep'),
      (value: string | undefined) => value != props.stepId,
    ),
  },
  milliQuantity: { required },
}))
const assignValidation = useVuelidate<AssignModel>(assignValidationRules, assignModel)
const assignMutation = useMutation<CreateStepInputMutation, CreateStepInputMutationVariables>(gql`
  mutation StepOutputsSectionAssignInput($command: CreateStepInput!) {
    product {
      createStepInput(command: $command) {
        id
      }
    }
  }
`)
const assignDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doAssign = async (stepOutputId: string, milliQuantity: number) => {
  assignModuleId.value = undefined
  assignModel.stepOutputId = stepOutputId
  assignModel.stepId = undefined
  assignModel.milliQuantity = milliQuantity

  await assignDialog.value?.open(() =>
    assignMutation.mutate({
      command: {
        ...assignModel,
        moduleId: assignModuleId.value,
      } as CreateStepInput,
    }),
  )
}

type UpdateModel = Partial<UpdateStepOutput>
const updateModelKeys = Object.keys(mockUpdateStepOutput())
const updateModel = reactive<UpdateModel>({})
const updateValidation = useVuelidate<UpdateModel>(
  {
    milliQuantity: { required },
  },
  updateModel,
)
const updateMutation = useMutation<UpdateStepOutputMutation, UpdateStepOutputMutationVariables>(gql`
  mutation UpdateStepOutput($command: UpdateStepOutput!) {
    product {
      updateStepOutput(command: $command) {
        id
      }
    }
  }
`)
const updateDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doUpdate = async (_: unknown, row: { item: LocalStepOutput }) => {
  updateModelKeys.forEach((k) => {
    ;(updateModel as Record<string, unknown>)[k] = row.item[k as keyof LocalStepOutput]
  })

  await updateDialog.value?.open(() =>
    updateMutation.mutate({ command: updateModel as UpdateStepOutput }).then(() => refetch()),
  )
}

const deleteMutation = useMutation<DeleteStepOutputMutation, DeleteStepOutputMutationVariables>(gql`
  mutation DeleteStepOutput($id: ID!) {
    product {
      deleteStepOutput(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof DeleteDialogNext>>()
const doDelete = async (item: LocalStepOutput) => {
  await deleteDialog.value?.open(t('entity.stepOutput.singular'), () =>
    deleteMutation.mutate({ id: item.id }).then(() => refetch()),
  )
}

const headers = [
  {
    key: 'article',
    title: t('entity.article.singular'),
  },
  {
    key: 'milliQuantity',
    title: t('entity.stepOutput.field.milliQuantity'),
    width: 50,
  },
  {
    key: 'actions',
    width: 100,
    align: 'end',
  },
]
const sort = [{ key: 'article', order: 'asc' }]
</script>

<template>
  <data-table
    :items="outputs"
    :items-per-page="-1"
    :headers="headers"
    :loading="loading"
    :sort-by="sort"
    density="comfortable"
    disable-sort
    @click:row="doUpdate"
  >
    <template #header.actions>
      <data-table-create-button @click="doCreate" />
    </template>
    <template #item.actions="{ item }: { item: LocalStepOutput }">
      <v-btn
        v-if="props.bopId"
        density="compact"
        variant="text"
        icon="input"
        :title="t('component.stepOutputsSection.buttonAssign')"
        @click.stop="doAssign(item.id, item.milliQuantity)"
      />
      <item-delete-icon @click="doDelete(item)" />
    </template>
    <template #item.article="{ item }: { item: LocalStepOutput }">
      <article-name :article="item.article" />
    </template>
    <template #item.milliQuantity="{ item }: { item: LocalStepOutput }">
      <article-quantity :milli-quantity="item.milliQuantity" :unit="item.article.unit" />
    </template>
  </data-table>

  <upsert-dialog
    ref="createDialog"
    type="create"
    :entity="t('entity.stepOutput.singular')"
    :validation="createValidation"
  >
    <article-picker
      v-model="createModel.articleId"
      required
      :label="t('entity.article.singular')"
      :validation="createValidation.articleId"
      with-create
    />
    <milli-quantity-field
      v-model="createModel.milliQuantity"
      required
      :validation="createValidation.milliQuantity"
      :label="t('entity.stepOutput.field.milliQuantity')"
    />
    <template v-if="props.bopId" #actions="{ save }">
      <v-btn :loading="createMutation.loading.value" @click="saveAndAssign(save)">
        {{ t('component.stepOutputsSection.buttonSaveAndAssign') }}
      </v-btn>
    </template>
  </upsert-dialog>

  <upsert-dialog
    ref="assignDialog"
    type="create"
    :entity="t('entity.stepInput.singular')"
    :validation="assignValidation"
  >
    <p class="text-medium-emphasis text-body-2 mb-4">
      {{ t('component.stepOutputsSection.assignHint') }}
    </p>
    <module-picker v-model="assignModuleId" :bop-id="props.bopId" />
    <step-picker
      v-model="assignModel.stepId"
      :module-id="assignModuleId"
      :validation="assignValidation.stepId"
    />
    <milli-quantity-field
      v-model="assignModel.milliQuantity"
      required
      :validation="assignValidation.milliQuantity"
      :label="t('entity.stepInput.field.milliQuantity')"
    />
  </upsert-dialog>

  <upsert-dialog
    ref="updateDialog"
    type="update"
    :entity="t('entity.stepOutput.singular')"
    :validation="updateValidation"
  >
    <milli-quantity-field
      v-model="updateModel.milliQuantity"
      :validation="updateValidation.milliQuantity"
      required
      :label="t('entity.stepOutput.field.milliQuantity')"
    />
  </upsert-dialog>

  <async-delete-dialog ref="deleteDialog" />
</template>

<style scoped lang="scss"></style>
